/**
 * Redux slice (src/store/slices/walletSlice.js).
 *
 * Data flow (typical):
 * - UI dispatches actions/thunks from `src/store/actions/*`.
 * - `reducers` handles synchronous state updates.
 * - `extraReducers` reacts to thunk pending/fulfilled/rejected actions.
 */

import { createSlice } from "@reduxjs/toolkit";
import { 
  fetchWallet,
  fetchWalletBalance, 
  purchaseHours, 
  fetchWalletTransactions,
  bookLessonWithWallet,
  addHoursToWallet,
  deductHoursFromWallet,
  addPendingHoursToWallet,
  confirmPendingHours,
  useHours,
  fetchWalletHistory,
  fetchPackageWalletHistory,
} from '../actions/walletActions';

/**
 * Slice initial state.
 *
 * Keep this shape stable because it is consumed across many selectors/components.
 */
const initialState = {
  balance: 0,
  packageBalances: {},
  pendingHours: 0,
  transactions: [],
  packageslist: [],
  loading: false,
  error: null,
  walletHistoryRequestId: null,
  // New state for wallet history
  walletHistory: {
    childContext: null,
    summary: null,
    transactions: [],
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      itemsPerPage: 10
    }
  },
  packageHistory: {
    packageInfo: null,
    transactions: [],
    summary: null
  }
};

/**
 * RTK slice definition: walletSlice.
 */
const walletSlice = createSlice({
  name: "wallet",
  initialState,
  reducers: {
    /**
     * Reducer: resetWalletError.
     */
    resetWalletError: (state) => {
      state.error = null;
    },
  },
  /**
   * Reducer: extraReducers.
   */
  extraReducers: (builder) => {
    builder
      // Fetch Wallet
      .addCase(fetchWallet.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: fetchWallet.fulfilled
      .addCase(fetchWallet.fulfilled, (state, action) => {
        state.balance = action.payload.balance;
        state.transactions = action.payload.transactions;
        state.loading = false;
      })
      // extraReducers handler: fetchWallet.rejected
      .addCase(fetchWallet.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Wallet Balance
      .addCase(fetchWalletBalance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: fetchWalletBalance.fulfilled
      .addCase(fetchWalletBalance.fulfilled, (state, action) => {
        state.balance = action.payload.balance;
        state.packageBalances = action.payload.packageBalances;
        state.loading = false;
      })
      // extraReducers handler: fetchWalletBalance.rejected
      .addCase(fetchWalletBalance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Purchase Hours
      .addCase(purchaseHours.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: purchaseHours.fulfilled
      .addCase(purchaseHours.fulfilled, (state, action) => {
        state.balance = action.payload.newBalance;
        state.packageBalances = {
          ...state.packageBalances,
          [action.payload.packageId]: action.payload.newPackageBalance
        };
        state.loading = false;
      })
      // extraReducers handler: purchaseHours.rejected
      .addCase(purchaseHours.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Use Hours
      .addCase(useHours.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: useHours.fulfilled
      .addCase(useHours.fulfilled, (state, action) => {
        state.balance = action.payload.newBalance;
        state.packageBalances = {
          ...state.packageBalances,
          [action.payload.packageId]: action.payload.newPackageBalance
        };
        state.loading = false;
      })
      // extraReducers handler: useHours.rejected
      .addCase(useHours.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Book Lesson With Wallet
      .addCase(bookLessonWithWallet.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: bookLessonWithWallet.fulfilled
      .addCase(bookLessonWithWallet.fulfilled, (state, action) => {
        state.balance = action.payload.newBalance;
        state.loading = false;
      })
      // extraReducers handler: bookLessonWithWallet.rejected
      .addCase(bookLessonWithWallet.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Wallet Transactions
      .addCase(fetchWalletTransactions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: fetchWalletTransactions.fulfilled
      .addCase(fetchWalletTransactions.fulfilled, (state, action) => {
        state.transactions = action.payload;
        state.loading = false;
      })
      // extraReducers handler: fetchWalletTransactions.rejected
      .addCase(fetchWalletTransactions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Add Hours To Wallet
      .addCase(addHoursToWallet.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: addHoursToWallet.fulfilled
      .addCase(addHoursToWallet.fulfilled, (state, action) => {
        state.balance = action.payload.newBalance;
        state.loading = false;
      })
      // extraReducers handler: addHoursToWallet.rejected
      .addCase(addHoursToWallet.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Deduct Hours From Wallet
      .addCase(deductHoursFromWallet.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: deductHoursFromWallet.fulfilled
      .addCase(deductHoursFromWallet.fulfilled, (state, action) => {
        state.balance = action.payload.newBalance;
        state.loading = false;
      })
      // extraReducers handler: deductHoursFromWallet.rejected
      .addCase(deductHoursFromWallet.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Add Pending Hours To Wallet
      .addCase(addPendingHoursToWallet.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: addPendingHoursToWallet.fulfilled
      .addCase(addPendingHoursToWallet.fulfilled, (state, action) => {
        state.pendingHours = action.payload;
        state.loading = false;
      })
      // extraReducers handler: addPendingHoursToWallet.rejected
      .addCase(addPendingHoursToWallet.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Confirm Pending Hours
      .addCase(confirmPendingHours.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: confirmPendingHours.fulfilled
      .addCase(confirmPendingHours.fulfilled, (state) => {
        state.balance += state.pendingHours;
        state.pendingHours = 0;
        state.loading = false;
      })
      // extraReducers handler: confirmPendingHours.rejected
      .addCase(confirmPendingHours.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // New Wallet History Cases
      .addCase(fetchWalletHistory.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.walletHistoryRequestId = action.meta?.requestId || null;
      })
      // extraReducers handler: fetchWalletHistory.fulfilled
      .addCase(fetchWalletHistory.fulfilled, (state, action) => {
        if (state.walletHistoryRequestId && action.meta?.requestId !== state.walletHistoryRequestId) return;
        const payload = action.payload && typeof action.payload === 'object' ? action.payload : {};
        state.walletHistory = {
          childContext: payload.childContext || null,
          summary: payload.summary || null,
          transactions: Array.isArray(payload.transactions) ? payload.transactions : [],
          pagination: payload.pagination || initialState.walletHistory.pagination
        };
        state.loading = false;
      })
      // extraReducers handler: fetchWalletHistory.rejected
      .addCase(fetchWalletHistory.rejected, (state, action) => {
        if (state.walletHistoryRequestId && action.meta?.requestId !== state.walletHistoryRequestId) return;
        state.loading = false;
        state.error = action.payload;
      })

      // Package Wallet History Cases
      .addCase(fetchPackageWalletHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: fetchPackageWalletHistory.fulfilled
      .addCase(fetchPackageWalletHistory.fulfilled, (state, action) => {
        state.packageHistory = {
          packageInfo: action.payload.packageInfo,
          transactions: action.payload.transactions,
          summary: action.payload.summary
        };
        state.loading = false;
      })
      // extraReducers handler: fetchPackageWalletHistory.rejected
      .addCase(fetchPackageWalletHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { resetWalletError } = walletSlice.actions;
export default walletSlice.reducer;
